// React and hooks
import { useEffect, useRef, useState } from "react";

// Contexts and hooks (used in hook body)
import {
  useCallingContext,
  useInCallContext,
} from "@/hooks/dialer/use-dialer-context";
import { useDispositionSectionContext } from "@/modules/pipeline/account-details/sections/disposition-section/context";

// API and types
import { glencocoClientAPI } from "@/api/glencoco";
import {
  AddCallDispositionV2ParamsI,
  GetCallByTwilioCallSIdResponseI,
} from "@/api/routes/dialer";
import { AxiosResponse } from "axios";
import { FormValidationSchemaI } from "./interface";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";

// Constants (used in logic)
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { ERROR_CATEGORIES, ERRORS } from "@/constants/errors";
import {
  DISPOSITIONS as ALL_DISPOSITIONS,
  DISPOSITION_STATUSES,
  DISPOSITIONS_GLENX,
  NEXT_TOUCH_TIME_OPTIONS,
  NEXT_TOUCH_TIME_OPTIONS_DAYJS_DURATION_MAP,
  PROSPECT_DISPOSITION_TYPES,
} from "shared/lib/constants/dispositions";
import { GLENX_NEXT_TOUCH_TIME_OPTIONS } from "@/modules/pipeline/account-details/sections/disposition-section/constants";

// Helpers and utilities
import { dd } from "@/helpers/datadog";
import { dispatchCustomEvent } from "@/helpers/events";
import { DayJs } from "shared/lib/helpers/date";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";
import toast from "react-hot-toast";
import { Call } from "@twilio/voice-sdk";
import { LOG_CATEGORIES } from "@/constants/logs";

const formatNextTouchTime = (
  selectedOption?: ValueOfObjectFields<typeof NEXT_TOUCH_TIME_OPTIONS> | ""
) => {
  if (
    !selectedOption ||
    [NEXT_TOUCH_TIME_OPTIONS.CUSTOM as string].includes(selectedOption)
  )
    return undefined;

  const nextTouchTimeDate = DayJs()
    .add(
      DayJs.duration(
        NEXT_TOUCH_TIME_OPTIONS_DAYJS_DURATION_MAP[selectedOption as string]
      )
    )
    .toISOString();

  return nextTouchTimeDate;
};

export const useDispositionSubmission = ({
  isDispoConversation,
  nextTouchTimeCustom,
  onSuccess,
}: {
  isDispoConversation?: boolean;
  nextTouchTimeCustom?: string;
  onSuccess?: (data: FormValidationSchemaI) => void;
}) => {
  const [isPending, setIsPending] = useState(false);

  const { customDispositions } = useDispositionSectionContext();
  const customDispositionsRef = useRef(customDispositions);
  useEffect(() => {
    customDispositionsRef.current = customDispositions;
  }, [customDispositions]);

  const callingContext = useCallingContext();
  const callingContextRef = useRef(callingContext);
  useEffect(() => {
    callingContextRef.current = callingContext;
  }, [callingContext]);

  const inCallContext = useInCallContext();
  const inCallContextRef = useRef(inCallContext);
  useEffect(() => {
    inCallContextRef.current = inCallContext;
  }, [inCallContext]);

  /**
   * If call id is not available in custom parameters
   * we need to fetch it by calling the API with the call SID that is guaranteed to be available
   */
  const getCallId = async (call?: Call) => {
    //------------ Primary Flow ------------
    let callId = call?.customParameters.get("call_id") as string;

    if (callId) return callId;

    //------------ Fallback Flow ------------
    // If call id is not available in custom parameters, we need to fetch it
    // by calling the API with the call SID that is guaranteed to be available
    dd.warn(
      `${LOG_CATEGORIES.DISPOSITION}[WARN] - Call ID is not available in custom parameters`
    );

    const API = glencocoClientAPI();
    const Resp = await API.getCallByTwilioCallSId(
      call?.parameters?.CallSid as string
    ).catch((e) => e);

    callId = (Resp.data as GetCallByTwilioCallSIdResponseI).call_id;

    if (Resp.status === 200 && callId) {
      return callId;
    }

    dd.error(
      `${ERROR_CATEGORIES.DISPOSITION} - Failed to fetch call ID by Twilio call SID`,
      {
        data: {
          call: call || "No available",
          callId: callId || "No available",
          twilioCallSid: call?.parameters?.CallSid || "No available",
          response: Resp,
        },
      }
    );

    return undefined;
  };

  const onSuccessDispositionSubmission = (data: FormValidationSchemaI) => {
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
    });

    onSuccess?.(data);
  };

  const onErrorDispositionSubmission = (resp: AxiosResponse) => {
    const call = callingContextRef.current.call;
    const campaign = callingContextRef.current.campaign;
    const contact = inCallContextRef.current.contact;
    const account = inCallContextRef.current.account;

    const errorCode = resp.data?.error_code;

    const errorMessage = errorCode
      ? ERRORS[errorCode as keyof typeof ERRORS]
      : "Failed to submit call disposition info";

    toast.error(errorMessage);

    dd.rum.error(`${ERROR_CATEGORIES.DISPOSITION} - ${errorMessage}`, {
      data: {
        call: call,
        customParameters: Object.fromEntries(call?.customParameters || []),
        campaignId: campaign?.id,
        contactId: contact?.id,
        accountId: account?.id,
        response: resp,
      },
    });
  };

  const handleDispoNoConversation = async (data: FormValidationSchemaI) => {
    const API = glencocoClientAPI();
    const call = callingContextRef.current.call;
    const campaign = callingContextRef.current.campaign;

    setIsPending(true);

    const callId = await getCallId(call);

    /**
     * NOTE
     *
     * In case user manually doesn't select custom disposition
     * we need to find the one that is locked and has the key
     * that matches the locked type of no_pick_up disposition
     * which by default is NO_PICK_UP
     */
    const custom_disposition_id =
      data?.custom_disposition_id ||
      customDispositionsRef.current?.find(
        (customDisposition) =>
          customDisposition?.is_locked === true &&
          customDisposition?.key === ALL_DISPOSITIONS.NO_PICK_UP
      )?.id;

    const Resp = await API.addCallDispositionV2(
      campaign?.id as string,
      callId as string,
      {
        had_conversation_with_prospect: false,
        custom_disposition_id,
        notes: data.note || "",
      }
    ).catch((e) => e);

    if (Resp.status === 200) {
      onSuccessDispositionSubmission(data);
    } else onErrorDispositionSubmission(Resp);

    setIsPending(false);
  };

  const handleDispoWithConversation = async (data: FormValidationSchemaI) => {
    const API = glencocoClientAPI();
    const call = callingContextRef.current.call;
    const campaign = callingContextRef.current.campaign;

    setIsPending(true);

    const callId = await getCallId(call);

    let nextTouchTimeProps = {};
    if (data.next_touch_time !== GLENX_NEXT_TOUCH_TIME_OPTIONS.NONE) {
      nextTouchTimeProps = {
        next_touch_time:
          nextTouchTimeCustom || formatNextTouchTime(data.next_touch_time),
      };
    }

    /**
     * NOTE
     *
     * In case user manually doesn't select custom disposition
     * and only select disposition type (persona)
     * thus intent to disqualify the prospect
     * we need to find custom disposition the one that is locked and has the key
     * that matches the locked type of not_qualified disposition
     * which by default is DO_NOT_CALL
     */
    const custom_disposition_id =
      data?.custom_disposition_id ||
      ([
        PROSPECT_DISPOSITION_TYPES.BAD_DATA as string,
        PROSPECT_DISPOSITION_TYPES.DO_NOT_CALL,
        PROSPECT_DISPOSITION_TYPES.NOT_RELEVANT,
      ].includes(data?.disposition_type as string) &&
        customDispositionsRef.current?.find(
          (customDisposition) =>
            customDisposition?.is_locked === true &&
            customDisposition?.status === DISPOSITION_STATUSES.DISQUALIFIED
        )?.id) ||
      undefined;

    let params: AddCallDispositionV2ParamsI = {
      had_conversation_with_prospect: true,
      notes: data?.note || "",

      prospect_disposition_type: data?.disposition_type,
      disposition: data?.disposition,
      custom_disposition_id,
      ...nextTouchTimeProps,
    };
    params = omitBy(params, isUndefined);

    const Resp = await API.addCallDispositionV2(
      campaign?.id as string,
      callId as string,
      /**
       * If next touch time is undefined
       * it shouldn't be included in request
       */
      params
    ).catch((e) => e);

    if (Resp.status === 200) {
      onSuccessDispositionSubmission(data);
    } else onErrorDispositionSubmission(Resp);

    setIsPending(false);
  };

  const isMeetingBooked = (data: FormValidationSchemaI) =>
    data?.disposition === DISPOSITIONS_GLENX.MEETING_BOOKED;

  const handleDispoMeetingBooked = async (data: FormValidationSchemaI) => {
    const API = glencocoClientAPI();
    const call = callingContextRef.current.call;
    const campaign = callingContextRef.current.campaign;

    const callId = call?.customParameters.get("call_id") as string;

    let params: AddCallDispositionV2ParamsI = {
      had_conversation_with_prospect: true,
      notes: data?.note || "",

      prospect_disposition_type: data?.disposition_type,
      // disposition: data?.disposition,

      x_book_meeting: {
        meeting_time: data?.meeting_time as string,
        account_executive_id: data?.account_executive_id as string,
      },
    };
    params = omitBy(params, isUndefined);

    setIsPending(true);

    const Resp = await API.addCallDispositionV2(
      campaign?.id as string,
      callId,

      params
    ).catch((e) => e);

    if (Resp.status === 200) {
      onSuccessDispositionSubmission(data);
    } else onErrorDispositionSubmission(Resp);

    setIsPending(false);
  };

  const handleDipositionSubmission = async (data: FormValidationSchemaI) => {
    if (!isDispoConversation) {
      handleDispoNoConversation(data);
      return;
    }

    if (isMeetingBooked(data)) {
      handleDispoMeetingBooked(data);
      return;
    }

    handleDispoWithConversation(data);
  };

  return {
    handleDipositionSubmission,
    isPending,
  };
};
